import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { StaticQuery, graphql } from "gatsby"

import { Navigation } from "."

// Styles
import "../../styles/app.css"

const siteSettings = {
    title: "Open Racer",
    lang: "en",
    codeinjection_styles: "", // Add any custom styles here if needed
}

const footerStories = [
    {
        href: "/how-to-get-faster-at-go-karting",
        image: "/images/blog/2021/07/go-kart-track-aerial.jpg",
        imageAlt: "Aerial view of a kart circuit",
        meta: "Track craft · 7 min",
        title: "How to Get Faster at Go Karting",
        description:
            "A repeatable approach to braking points, corner speed and faster exits.",
        featured: true,
    },
    {
        href: "/your-gopro-might-be-your-best-data-logger",
        image: "/images/blog/2021/07/go-karting-360-shot.jpeg",
        imageAlt: "GoPro camera mounted for karting telemetry",
        meta: "Telemetry · 5 min",
        title: "Your GoPro Might Be Your Best Data Logger",
        description:
            "Use the camera you already own to understand what happens on every lap.",
    },
    {
        href: "/unveiling-the-thrills-of-go-karting-insights-from-the-2023-survey",
        image: "/images/blog/2023/11/Unveiling-the-Thrills-of-Go-Karting_-Insights-from-the-2023-Survey-Large.jpg",
        imageAlt: "Open Racer karting community survey",
        meta: "Community · 8 min",
        title: "What Racers Told Us About Where and Why They Drive",
        description:
            "Patterns and preferences from the Open Racer karting survey.",
    },
]

const footerLinkGroups = [
    {
        title: "Find a track",
        links: [
            ["Karting tracks", "/go-karting-tracks"],
            ["Motorsport circuits", "/tracks"],
            ["Popular cities", "/motorsport-countries"],
        ],
    },
    {
        title: "Get faster",
        links: [
            ["Racing line guides", "/tag/getting-faster"],
            ["Racing Line Editor", "/racing-line-editor"],
            ["Community lines", "/racing-line-editor#latest-lines"],
        ],
    },
    {
        title: "Racing tools",
        links: [
            ["iRacing Telemetry", "/iracing"],
            ["GoPro Importer", "/gopro-racing-line-importer"],
            ["Alfano Importer", "/alfano-racing-line-importer"],
        ],
    },
    {
        title: "For track owners",
        links: [
            ["Claim your business", "/go-karting-tracks"],
            ["Sponsor Open Racer", "/sponsorship"],
            ["Add a missing track", "/suggest-track"],
            ["Track booking software", "/go-kart-race-booking-software"],
        ],
    },
]

/**
 * Main layout component
 *
 * The Layout component wraps around each page and template.
 * It also provides the header, footer as well as the main
 * styles, and meta data for each page.
 *
 */
const DefaultLayout = ({
    data,
    children,
    bodyClass,
    image,
    title,
    hideHeader,
    hideFooter,
}) => {
    const site = siteSettings

    return (
        <>
            <Helmet>
                <html lang={site.lang} />
                <style type="text/css">{`${site.codeinjection_styles}`}</style>
                <body className={bodyClass} />
            </Helmet>

            <div className="site-wrapper">
                {!hideHeader && <Navigation />}

                <main className="site-main outer">
                    <div className="inner">
                        {title && (
                            <header className="post-full-header">
                                <h1 className="post-full-title">{title}</h1>
                            </header>
                        )}

                        {image && (
                            <figure
                                className="post-full-image"
                                style={{ backgroundImage: `url(${image})` }}
                            />
                        )}
                        {children}
                    </div>
                </main>

                {!hideFooter && (
                    <footer className="or-content-footer">
                        <section
                            className="or-footer-stories outer"
                            aria-labelledby="footer-stories-title"
                        >
                            <div className="inner">
                                <div className="or-footer-section-heading">
                                    <div>
                                        <p className="or-footer-eyebrow">
                                            From the paddock
                                        </p>
                                        <h2 id="footer-stories-title">
                                            Stories to make you faster
                                        </h2>
                                        <p>
                                            Practical guides, track intelligence
                                            and real racing data for your next
                                            session.
                                        </p>
                                    </div>
                                    <a
                                        className="or-footer-all-link"
                                        href="/tag/getting-faster"
                                    >
                                        Browse all stories
                                    </a>
                                </div>

                                <div className="or-footer-story-grid">
                                    {footerStories.map((story) => (
                                        <a
                                            className={`or-footer-story${
                                                story.featured
                                                    ? " or-footer-story-featured"
                                                    : ""
                                            }`}
                                            href={story.href}
                                            key={story.href}
                                        >
                                            <figure>
                                                <img
                                                    src={story.image}
                                                    alt={story.imageAlt}
                                                    loading="lazy"
                                                />
                                            </figure>
                                            <p className="or-footer-story-meta">
                                                {story.meta}
                                            </p>
                                            <h3>{story.title}</h3>
                                            <p className="or-footer-story-description">
                                                {story.description}
                                            </p>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </section>

                        <section
                            className="or-footer-explore outer"
                            aria-labelledby="footer-explore-title"
                        >
                            <div className="inner or-footer-explore-layout">
                                <div className="or-footer-explore-intro">
                                    <p className="or-footer-eyebrow">
                                        Keep exploring
                                    </p>
                                    <h2 id="footer-explore-title">
                                        Explore racing
                                    </h2>
                                    <p>
                                        Find somewhere to race, study a better
                                        line, or turn your own telemetry into
                                        something useful.
                                    </p>
                                    <a
                                        className="or-footer-primary-link"
                                        href="/go-karting-tracks"
                                    >
                                        Find a track near you
                                    </a>
                                </div>

                                <div>
                                    <nav
                                        className="or-footer-link-groups"
                                        aria-label="Explore Open Racer"
                                    >
                                        {footerLinkGroups.map((group) => (
                                            <section
                                                className="or-footer-link-group"
                                                key={group.title}
                                            >
                                                <h3>{group.title}</h3>
                                                {group.links.map(
                                                    ([label, href]) => (
                                                        <a
                                                            href={href}
                                                            key={href}
                                                        >
                                                            {label}
                                                        </a>
                                                    )
                                                )}
                                            </section>
                                        ))}
                                    </nav>
                                    <div className="or-footer-owner-note">
                                        <div>
                                            <strong>
                                                Run or represent a circuit?
                                            </strong>
                                            <p>
                                                Keep your listing accurate and
                                                help racers discover your venue.
                                            </p>
                                        </div>
                                        <a href="/go-karting-tracks">
                                            Claim your track
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="site-footer outer">
                            <div className="inner">
                                <div className="or-footer-main">
                                    <div>
                                        <a className="or-footer-brand" href="/">
                                            {site.title}
                                        </a>
                                        <p>
                                            An open place to discover tracks,
                                            explore racing lines and learn from
                                            real driving data.
                                        </p>
                                        <a
                                            className="or-footer-award"
                                            href="/racing-line-editor"
                                            aria-label="Open Racer Racing Line Editor, Google Maps Platform Awards winner, 2025"
                                        >
                                            <img
                                                src="/images/google-maps-platform-awards-2025.png"
                                                alt=""
                                                width="790"
                                                height="156"
                                                loading="lazy"
                                            />
                                            <span>
                                                <strong>
                                                    Google Maps Platform Awards
                                                    winner
                                                </strong>
                                                <small>
                                                    Racing Line Editor · 2025
                                                </small>
                                            </span>
                                        </a>
                                    </div>
                                    <nav
                                        className="or-footer-final-links"
                                        aria-label="Open Racer footer"
                                    >
                                        <div>
                                            <h3>Discover</h3>
                                            <a href="/tag/getting-faster">
                                                Blog
                                            </a>
                                            <a href="/pricing">Pricing</a>
                                        </div>
                                        <div>
                                            <h3>Account</h3>
                                            <a href="/profile">Your profile</a>
                                            <a href="/racing-line-editor">
                                                Your racing lines
                                            </a>
                                        </div>
                                    </nav>
                                </div>
                                <div className="or-footer-bottom">
                                    <span>
                                        © {new Date().getFullYear()} Open Racer
                                    </span>
                                </div>
                            </div>
                        </section>
                    </footer>
                )}
            </div>
        </>
    )
}

DefaultLayout.propTypes = {
    children: PropTypes.node.isRequired,
    bodyClass: PropTypes.string,
    hideHeader: PropTypes.bool,
    hideFooter: PropTypes.bool,
    isHome: PropTypes.bool,
    data: PropTypes.shape({
        file: PropTypes.object,
    }).isRequired,
}

const DefaultLayoutSettingsQuery = (props) => (
    <StaticQuery
        query={graphql`
            query GhostSettings {
                file(relativePath: { eq: "ghost-icon.png" }) {
                    childImageSharp {
                        fixed(width: 30, height: 30) {
                            ...GatsbyImageSharpFixed
                        }
                    }
                }
            }
        `}
        render={(data) => <DefaultLayout data={data} {...props} />}
    />
)

export default DefaultLayoutSettingsQuery
